import {
  FirebaseAuthTypes,
  signInWithEmailAndPassword as signInWithEmailAndPasswordMobile
} from '@react-native-firebase/auth';
import { router } from 'expo-router';
import { Auth, signInWithEmailAndPassword as signInWithEmailAndPasswordWeb } from 'firebase/auth';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';

import { SIGN_IN_BUTTON_BOTTOM } from '@/constants/layout';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { auth } from '@/firebaseConfig';
import { NpoLoginData, npoLoginResolver } from '@/lib/validations/AuthSchema';
import { i18n } from '@/utils/i18n';
import { handleEmailSignInError } from '@/utils/loginErrorsHandler';

import BrandedButton from '../buttons/BrandedButton';
import TextInputField from '../inputs/TextInputField';
import TermsPrivacyText from '../TermsPrivacyText';
import Text from '../Text';

const defaultValues: NpoLoginData = {
  email: '',
  password: ''
};

const NpoLoginForm = () => {
  const { showError } = useEndFlowModal();
  const [isPending, setIsPending] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isValid, submitCount, errors },
    setFocus,
    setError
  } = useForm<NpoLoginData>({
    defaultValues: defaultValues,
    mode: 'onSubmit',
    resolver: npoLoginResolver
  });

  //Locales
  const forgotPassword = i18n.t('sign_in.nonProfit.forgot_password.forgot_password');
  const toHaveALoginAsNpo = i18n.t('sign_in.nonProfit.login.to_have_login_as_npo');
  const claimNpo = i18n.t('sign_in.nonProfit.login.claim_npo');
  const email = i18n.t('mix.email');
  const password = i18n.t('mix.password');

  const onSubmit: SubmitHandler<NpoLoginData> = async (data) => {
    setIsPending(true);
    try {
      const emailValue = data.email.toLocaleLowerCase();

      if (Platform.OS === 'web') {
        signInWithEmailAndPasswordWeb(auth() as Auth, emailValue, data.password)
          .then((_) => {
            setDisabled(true);
          })
          .catch((error) => {
            const { title, message } = handleEmailSignInError(error);
            showError({ title, message });
          })
          .finally(() => {
            setIsPending(false);
          });
      } else {
        signInWithEmailAndPasswordMobile(auth() as FirebaseAuthTypes.Module, emailValue, data.password)
          .then((_) => {
            setDisabled(true);
          })
          .catch((error) => {
            const { title, message } = handleEmailSignInError(error);
            showError({ title, message });
          })
          .finally(() => {
            setIsPending(false);
          });
      }
    } catch (error) {
      setError('root', { message: `${error}` });
      setIsPending(false);
      setDisabled(false);
    }
  };

  return (
    <View style={styles.formContainer}>
      <View style={styles.inputsContainer}>
        <Text fontWeight="700">
          {toHaveALoginAsNpo}
          <Text
            fontWeight="700"
            style={{ color: '#5A88F4' }}
            onPress={() => router.navigate('/login/nonProfit/findNpo')}>
            {` ${claimNpo}`}
          </Text>
        </Text>
        <TextInputField
          name="email"
          control={control}
          placeholder={email}
          type="email-address"
          returnKeyLabel="next"
          returnKeyType="next"
          onSubmitEditing={() => setFocus('password')}
        />
        <TextInputField
          name="password"
          control={control}
          placeholder={password}
          type="password"
          returnKeyLabel="done"
          returnKeyType="done"
          onSubmitEditing={() => handleSubmit(onSubmit)}
        />
        <TermsPrivacyText />
      </View>
      {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

      <View style={styles.buttonsSection}>
        <BrandedButton
          onPress={handleSubmit(onSubmit)}
          disabled={(!isValid && submitCount > 0) || disabled}
          loading={isPending}>
          {i18n.t('mix.continue')}
        </BrandedButton>
        <TouchableOpacity onPress={() => router.navigate('/login/nonProfit/forgotPassword')}>
          <Text style={styles.centerText}>{forgotPassword}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignContent: 'space-between'
  },

  inputsContainer: {
    gap: 15
  },

  buttonsSection: {
    marginBottom: SIGN_IN_BUTTON_BOTTOM,
    flexDirection: 'column',
    rowGap: 25
  },

  centerText: {
    textAlign: 'center'
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});

export default NpoLoginForm;
